import React, { useEffect, useRef } from 'react';
import Dictaphone from '../../speechRecognition/speechRecognition.js';
import fusotransparent from '../../assets/fuso-transparent.svg';
import sendIcon from '../../assets/sendBtnHR.svg';
import { useChatUi } from '../ChatContext';
import { AttachButton } from './Attachments';

const MAX_HEIGHT = 160;

/**
 * Message input. Keeps `textarea.input-question` and `button.send-button(.awaiting-response)` for the
 * batch script. Enter sends unless an IME composition is active (Japanese input confirms with Enter);
 * Shift+Enter is a newline. While an answer streams, the send button becomes Stop.
 */
export default function Composer({ value, onChange, onSend, onStop, busy, onAttach, maxLength = 2000 }) {
  const { t, langOpt } = useChatUi();
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (e.nativeEvent.isComposing || e.keyCode === 229) return; // IME conversion
    e.preventDefault();
    if (!busy) onSend();
  };

  const tooLong = value.length > maxLength;

  return (
    <div className={`question cv-composer${busy ? ' is-busy' : ''}`}>
      <img className="input-logo" src={fusotransparent} alt="" aria-hidden="true" />
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('How can I assist you today?')}
        className="input-question"
        aria-label={t('How can I assist you today?')}
        aria-invalid={tooLong || undefined}
      />
      {tooLong && <span className="cv-composer__count">{value.length}/{maxLength}</span>}
      {onAttach && <AttachButton onFiles={onAttach} />}
      <Dictaphone setQuestion={onChange} langOpt={langOpt} />
      <button
        type="button"
        onClick={() => (busy ? onStop() : onSend())}
        className={`send-button${busy ? ' awaiting-response cv-stop' : ''}`}
        title={busy ? t('Stop generating') : t('Send message')}
        aria-label={busy ? t('Stop generating') : t('Send message')}
      >
        {busy ? (
          <svg className="cv-stop__icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
          </svg>
        ) : (
          <img src={sendIcon} alt="" className="send-icon" />
        )}
      </button>
    </div>
  );
}
